import Swiper from "swiper";
import { Autoplay, EffectCreative, Navigation, Pagination } from 'swiper/modules';
import { type Module } from "~/foundation/Bootstrapper/types";
import { breakpointIndex, currentBreakpoint, onWindowResize } from "~/foundation/Events/onWindowResize";
import { isMobile } from "~/foundation/Helpers/isMobile";

const THEME_REGEX = /theme-\S+/;

// Global reference to control autoplay from outside
let globalSwiperInstance: Swiper | null = null;

export const topStorySlider: Module = (container: HTMLElement) => {
	const swiperContainer = container.querySelector<HTMLElement>(".swiper-container");
	const header = document.querySelector<HTMLElement>("header");
	const pagination = container.querySelector<HTMLElement>(".swiper-pagination");
	const nextButton = container.querySelector<HTMLElement>(".swiper-button-next");

	if (!swiperContainer) return;

	// Track autoplay state
	let isAutoplayEnabled = true;

	const swiper = new Swiper(swiperContainer, {
		modules: [Navigation, Pagination, EffectCreative, Autoplay],
		direction: "vertical",
		effect: "creative",
		loop: true,
		noSwiping: true,
		noSwipingClass: "swiper-slide",
		speed: 950,
		autoplay: {
			delay: 4000,
			disableOnInteraction: true,
		},
		creativeEffect: {
			prev: {
				translate: [0, "60%", -1],
			},
			next: {
				translate: [0, "-100%", 0],
			},
		},
		pagination: {
			el: pagination,
			clickable: true,
		},
		navigation: {
			nextEl: nextButton,
		},
		on: {
			init: (swiper) => {
				swiper.el.classList.remove("init");
			}
		}
	});

	// Store global reference for external control
	globalSwiperInstance = swiper;

	function getTheme(element: HTMLElement | Element | null): string {
		const theme = element?.classList.value.match(THEME_REGEX) || null;
		if (!theme) return "";
		return theme[0];
	}

	function removeTheme(elements: (HTMLElement | Element | null)[]): void {
		elements.forEach(element => {
			const theme = element?.classList.value.match(THEME_REGEX) || null;
			if (!theme) return;
			element?.classList.remove(theme[0]);
		})
	}

	function setTheme(theme: string, elements: (HTMLElement | Element | null)[]) {
		removeTheme(elements);
		elements.forEach(element => element?.classList.add(theme));
	}

	function handleScroll() {
		const sliderRect = container.getBoundingClientRect();
		const sliderBottom = sliderRect.bottom;
		const viewportHeight = window.innerHeight;

		// Check if slider is visible in viewport
		const isSliderVisible = sliderBottom > 0 && sliderRect.top < viewportHeight;

		// Check if user has scrolled past the slider
		const hasScrolledPastSlider = sliderRect.top < -sliderRect.height;

		// Disable autoplay if slider is not visible or user has scrolled past it
		const shouldDisableAutoplay = !isSliderVisible || hasScrolledPastSlider;

		if (shouldDisableAutoplay && isAutoplayEnabled) {
			swiper.autoplay.stop();
			isAutoplayEnabled = false;
		} else if (!shouldDisableAutoplay && !isAutoplayEnabled) {
			swiper.autoplay.start();
			isAutoplayEnabled = true;
		}
	}

	function init() {
		const slide = container.querySelector(`[data-swiper-slide-index="0"]`);
		const theme = getTheme(slide);
		const elements = isMobile() ? [header] : [header, pagination]
		setTheme(theme, elements);

		swiper.on("slideChange", (swiper) => {
			const slide = container.querySelector(`[data-swiper-slide-index="${swiper.realIndex}"]`);
			const theme = getTheme(slide);
			setTheme(theme, elements);
		});

		if (currentBreakpoint >= breakpointIndex.sm) {
			pagination?.classList.remove("swiper-pagination-horizontal");
			pagination?.classList.add("swiper-pagination-vertical");

		} else {
			pagination?.classList.remove("swiper-pagination-vertical");
			pagination?.classList.add("swiper-pagination-horizontal");
		}
	}

	init();

	// Add scroll event listener
	window.addEventListener('scroll', handleScroll, { passive: true });

	// Listen for custom event to pause autoplay
	const handlePauseEvent = () => {
		if (globalSwiperInstance) {
			globalSwiperInstance.autoplay.stop();
		}
	};
	document.addEventListener('pauseTopStorySlider', handlePauseEvent);

	// Listen for custom event to resume autoplay
	const handleResumeEvent = () => {
		if (globalSwiperInstance) {
			globalSwiperInstance.autoplay.start();
		}
	};
	document.addEventListener('resumeTopStorySlider', handleResumeEvent);

	onWindowResize(init);

	// Return cleanup function
	return () => {
		window.removeEventListener('scroll', handleScroll);
		document.removeEventListener('pauseTopStorySlider', handlePauseEvent);
		document.removeEventListener('resumeTopStorySlider', handleResumeEvent);
		globalSwiperInstance = null;
	};
}

// External control functions
export const pauseTopStorySliderAutoplay = () => {
	if (globalSwiperInstance) {
		globalSwiperInstance.autoplay.stop();
	}
};

export const resumeTopStorySliderAutoplay = () => {
	if (globalSwiperInstance) {
		globalSwiperInstance.autoplay.start();
	}
};

export default topStorySlider;